import { ManageInvestorDetailsContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/InvestorDetails';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { SendInvitationModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendInvitationModal/SendInvitationModal';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { postMipInvestorsInvitations } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import {
	CHIP_THEME,
	Chips as ChipsV2,
	COLOR,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { DashCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/DashCard/DashCard';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { InvestorInvestmentStatus } from '@dealstack/shared/lib/common/business/investor';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { isEmpty } from 'lodash';
import { useContext, useState } from 'react';
import { InvestorInvitationStatus, SendInvestorInviteDto } from '@dealstack/shared/lib/generated';

interface Props {
	className?: string;
}

export const DashcardAccount = ({ className }: Props) => {
	const mipManageController = useMIPManageContext();
	const manageInvestorDetailsController = useContext(ManageInvestorDetailsContext);
	const { investorDetailsData, investments, investorId, refreshMIPInvestorDetailsData } =
		manageInvestorDetailsController;
	const [isSendInvitationModalOpened, setIsSendInvitationModalOpened] = useState(false);
	const tMipManage = useTranslationMipManage();
	const tDefault = useTranslationDefault();
	const postMIPInvestorsInvitationsRequest = useWrappedRequest(postMipInvestorsInvitations);

	const onSendInvestorInvitations = async (invites: SendInvestorInviteDto): Promise<void> => {
		if (!investorId) {
			return;
		}

		await postMIPInvestorsInvitationsRequest
			.fetch(invites)
			.onSuccess(() => {
				refreshMIPInvestorDetailsData();
			})
			.onLocalError((responseError) => {
				const errorMessage = isStandardBackendError(responseError)
					? tDefault(`error_codes.${responseError.error?.code}`)
					: tMipManage('send_modal.toast.error.message');
				showToastV2({
					title: errorMessage,
					state: 'error',
				});
				throw responseError;
			})
			.concludeWithPromise();
	};

	const hasReleasedStatus =
		investorDetailsData?.latestInvestmentStatus === InvestorInvestmentStatus.RELEASED;

	const isInvestmentEmpty =
		isEmpty(investments?.investmentDetails) && isEmpty(investments?.pendingInvestmentDetails);
	const hasRevokedInvestment = isInvestmentEmpty && hasReleasedStatus;
	const invitationStatus = investorDetailsData?.investorInvitation?.status;

	const mappedInvitationStatusWithUI = {
		[InvestorInvitationStatus.ACTIVE]: {
			icon: 'tick',
			theme: CHIP_THEME.LIGHT,
			text: tMipManage('investors.dashcards.account.active'),
		},
		[InvestorInvitationStatus.INVITED]: {
			icon: 'mail',
			theme: CHIP_THEME.DARK,
			text: tMipManage('investors.dashcards.account.invited'),
		},
		revoke: {
			icon: 'mail',
			theme: CHIP_THEME.DARK,
			text: tMipManage('investors.dashcards.account.not_invited'),
		},
	};
	const renderInvitationItem = (props?: { icon: string; theme: CHIP_THEME; text: string }) => {
		if (!props) {
			return null;
		}
		return (
			<ChipsV2
				prefixIcon={props.icon}
				size="s"
				chipTheme={props.theme}
				color={COLOR.GREY}
				data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_INVITATION_STATUS}
			>
				{props.text}
			</ChipsV2>
		);
	};
	const renderInvitationStatus = () => {
		if (hasRevokedInvestment) {
			return renderInvitationItem(mappedInvitationStatusWithUI.revoke);
		}

		if (invitationStatus) {
			return renderInvitationItem(mappedInvitationStatusWithUI[invitationStatus]);
		}

		return null;
	};

	const renderInvitationButton = () => {
		if (invitationStatus === InvestorInvitationStatus.NOT_INVITED && !hasRevokedInvestment) {
			return (
				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_INVITATION_BUTTON}
				>
					<ButtonV2
						size="xs"
						disabled={mipManageController?.disableSendInvestorInvitation}
						onClick={() => setIsSendInvitationModalOpened(true)}
						variant="primary"
						icon="mail"
						buttonTheme="light"
						leftIcon
					>
						{tMipManage('investors.actions.send_invite')}
					</ButtonV2>
				</UIElementIdentifier>
			);
		}

		return null;
	};

	const isNotInvitedTitle =
		hasRevokedInvestment || invitationStatus === InvestorInvitationStatus.NOT_INVITED;

	const invitationTitle = isNotInvitedTitle
		? tMipManage('investors.dashcards.account.not_invited')
		: tMipManage('investors.dashcards.account.invited');

	return (
		<>
			<DashCard className={className}>
				<DashCard.Top icon="mail" iconSize="sm" textVariant="sans/14/600">
					{invitationTitle}
				</DashCard.Top>
				<DashCard.Divider />
				<DashCard.Middle
					content={
						invitationStatus === InvestorInvitationStatus.INVITED
							? [
									tMipManage('investors.dashcards.account.invited_on', {
										date:
											formatStringDateToUI(
												investorDetailsData?.investorInvitation?.invitedAt,
												DATE_FORMATS.MONTH_DAY_YEAR
											) ?? LONG_DASH,
									}) as string,
								]
							: undefined
					}
					status={renderInvitationStatus()}
					button={renderInvitationButton()}
				/>
			</DashCard>
			{isSendInvitationModalOpened && !!investorId && !!investorDetailsData && (
				<SendInvitationModal
					onClose={() => {
						setIsSendInvitationModalOpened(false);
					}}
					sendInvitation={onSendInvestorInvitations}
					isLoading={postMIPInvestorsInvitationsRequest.isLoading}
					investors={[
						{
							id: investorId,
							linkedContractsName: investorDetailsData?.linkedContractsName,
							person: {
								firstName: investorDetailsData.person.firstName ?? '',
								lastName: investorDetailsData.person.lastName ?? '',
								displayName: investorDetailsData.person.displayName,
								email: investorDetailsData.person.email ?? '',
							},
							willBeSSOUser: false,
						},
					]}
				/>
			)}
		</>
	);
};
